import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onUnmounted, Ref } from 'vue';
import store from '@/store';
import type { ListElements } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import { getCanvasElementTitle } from '@/utils/elementsUtils';
import CButton from '@/components/shared/CButton/CButton.vue';
import CInput from '@/components/shared/CInput';

// Types
type ListElementsWithMatch = ListElements & { matched?: boolean };

interface CInputRef {
    focus: () => void;
}

// Props & Emits

export default /*#__PURE__*/_defineComponent({
  __name: 'FindInLayers',
  props: {
    enable: { type: Boolean, default: true }
  },
  emits: ["close"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { enable: boolean };





// State
const searchQueryModel = ref('');
const searchQuery = ref('');
const selectedLayerIndex = ref(-1);
const showFindInLayers = ref(false);
const inputComponentRef: Ref<CInputRef | null> = ref(null);

// Computed
const treeItems = computed(() => store.state.storeLayers?.treeItems || []);

const filteredLayers = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    return query ? filterTreeItems(treeItems.value, query) : treeItems.value;
});

const totalCount = computed(() => countMatchedItems(filteredLayers.value));
const hasResults = computed(() => totalCount.value > 0);
const currentResultNumber = computed(() => Math.max(selectedLayerIndex.value + 1, 0));

// Helper Functions
const getElementName = (item: ListElements): string => {
    if (item.element?.name) return item.element.name;
    if (item.type === ListElementType.CanvasElement && item.element) {
        return getCanvasElementTitle(item.element) || '';
    }
    return '';
};

const filterTreeItems = (items: ListElementsWithMatch[], query: string): ListElementsWithMatch[] => {
    return items.reduce<ListElementsWithMatch[]>((acc, item) => {
        const name = getElementName(item).toLowerCase();
        const nameMatches = name.includes(query);
        const filteredChildren = item.children?.length ? filterTreeItems(item.children, query) : [];

        if (nameMatches || filteredChildren.length > 0) {
            acc.push({
                ...item,
                matched: nameMatches && item.type !== ListElementType.PageEps,
                children: filteredChildren
            });
        }

        return acc;
    }, []);
};

const countMatchedItems = (items: ListElementsWithMatch[]): number => {
    return items.reduce((count, item) => {
        const itemCount = item.matched ? 1 : 0;
        const childrenCount = item.children?.length ? countMatchedItems(item.children) : 0;
        return count + itemCount + childrenCount;
    }, 0);
};

const getMatchedItemByIndex = (items: ListElementsWithMatch[], targetIndex: number): ListElementsWithMatch | null => {
    let currentIndex = -1;

    const findItem = (items: ListElementsWithMatch[]): ListElementsWithMatch | null => {
        for (const item of items) {
            if (item.matched && ++currentIndex === targetIndex) {
                return item;
            }

            if (item.children?.length) {
                const found = findItem(item.children);
                if (found) return found;
            }
        }
        return null;
    };

    return findItem(items);
};

// Actions
const selectLayerAtIndex = (index: number) => {
    if (index < 0 || !hasResults.value) return;

    const matchedItem = getMatchedItemByIndex(filteredLayers.value, index);
    if (!matchedItem?.element) return;

    const elementId = String(matchedItem.element.uuid || matchedItem.element.id);
    if (!elementId) return;

    store.dispatch('storeLayers/expandAndSelectElement', {
        elementId,
        scrollToElement: true
    });
};

const navigateResults = (direction: 'next' | 'prev') => {
    if (!hasResults.value) return;

    if (direction === 'next') {
        selectedLayerIndex.value = selectedLayerIndex.value >= totalCount.value - 1 ? 0 : selectedLayerIndex.value + 1;
    } else {
        selectedLayerIndex.value = selectedLayerIndex.value <= 0 ? totalCount.value - 1 : selectedLayerIndex.value - 1;
    }
};

const focusNext = () => navigateResults('next');
const focusPrevious = () => navigateResults('prev');

const resetSearch = () => {
    searchQuery.value = '';
    searchQueryModel.value = '';
    selectedLayerIndex.value = -1;
};

const handleClose = () => {
    showFindInLayers.value = false;
    emit('close');
};

const onUpdateSearchDecounced = (value: string | number) => {
    searchQuery.value = String(value);
};

const openFindPanel = () => {
    showFindInLayers.value = true;
    requestAnimationFrame(() => {
        inputComponentRef.value?.focus();
    });
};

const closeFindPanel = () => {
    showFindInLayers.value = false;
};

const toggleFindPanel = () => {
    if (showFindInLayers.value) {
        closeFindPanel();
    } else {
        openFindPanel();
    }
};

// Event Handlers
const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        toggleFindPanel();
    }
};

// Watchers
watch(filteredLayers, () => {
    selectedLayerIndex.value = -1;
    if (hasResults.value) {
        requestAnimationFrame(focusNext);
    }
});

watch(selectedLayerIndex, (newIndex) => {
    if (newIndex >= 0 && hasResults.value) {
        selectLayerAtIndex(newIndex);
    }
});

watch(showFindInLayers, (isVisible) => {
    if (!isVisible) {
        resetSearch();
    }
});

// Lifecycle
onMounted(() => {
    if (props.enable) {
        window.addEventListener('keydown', handleKeyDown);
    }
});

onUnmounted(() => {
    if (props.enable) {
        window.removeEventListener('keydown', handleKeyDown);
    }
});

expose({
    open: openFindPanel,
    close: closeFindPanel,
    toggle: toggleFindPanel,
    isOpen: () => showFindInLayers.value
});

return { __sfc: true,emit, props, searchQueryModel, searchQuery, selectedLayerIndex, showFindInLayers, inputComponentRef, treeItems, filteredLayers, totalCount, hasResults, currentResultNumber, getElementName, filterTreeItems, countMatchedItems, getMatchedItemByIndex, selectLayerAtIndex, navigateResults, focusNext, focusPrevious, resetSearch, handleClose, onUpdateSearchDecounced, openFindPanel, closeFindPanel, toggleFindPanel, handleKeyDown, CButton, CInput }
}

})